import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "./PaymentSuccessPage.css";
import { useDispatch, useSelector } from "react-redux";
import { getPaymentInfoAction } from "../../../actions/Payment/paymentAction";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

// Icons components
const CheckCircleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#d4af37"
    strokeWidth="2"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const CopyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const DownloadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const LinkedInIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="#0A66C2"
  >
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const CompanyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ffd700"
    strokeWidth="2"
  >
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
  </svg>
);

// Type can be: 'portfolio', 'certificate', 'bundle'
const PaymentSuccessPage = () => {
  const params = new URLSearchParams(window.location.search);
  const paymentId = params.get("payment_id");
  const { userInfo } = useSelector((state) => state.userLogin);
  const { paymentData } = useSelector((state) => state.getpaymentInfoStore);
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    if (userInfo && paymentId) {
      dispatch(getPaymentInfoAction(paymentId));
    }
  }, [userInfo, paymentId, dispatch]);

  const [copiedPortfolio, setCopiedPortfolio] = useState(false);
  const [copiedCertificate, setCopiedCertificate] = useState(false);

  // Derive what user actually bought from paymentData
  const hasCertificate = paymentData?.items?.certification?.exists;
  const hasPortfolio = paymentData?.items?.portfolio?.exists;

  // Decide which "type" of page to render
  let type = null;
  if (hasCertificate && hasPortfolio) type = "bundle";
  else if (hasCertificate) type = "certificate";
  else if (hasPortfolio) type = "portfolio";

  // Derive links from paymentData
  const portfolioLink =
    paymentData?.items?.portfolio?.portfolioUrl || "https://yourportfolio.dev";
  const certificateDownloadUrl =
    paymentData?.items?.certification?.certificateUrl ||
    "/api/download-certificate";

  const userName = userInfo?.name || "Developer";

  const portfolioLinkedInPost = `🚀 Excited to share my new developer portfolio!

I've just created my professional portfolio showcasing my projects, skills, and journey as a developer.

Recruiters and fellow developers, feel free to check it out and connect!

🔗 ${portfolioLink}

#WebDevelopment #Portfolio #Developer #OpenToWork #TechJobs`;

  const certificateLinkedInPost = `🎉 Just earned my Verified Frontend Certificate!

Thrilled to announce that I've completed my frontend development certification. This journey has been incredible, and I'm ready to take on new challenges!

Looking forward to connecting with recruiters and teams building amazing products.

#FrontendDevelopment #Certified #WebDev #JavaScript #React #OpenToWork`;

  const mixedCarouselPosts = [
    {
      id: 1,
      name: "Priya Sharma",
      role: "Senior Product Manager @ Tech Innovators",
      text: "Just shipped our biggest feature yet! 🚀 After 3 months of hard work with an amazing cross-functional team, we launched AI-powered recommendations that increased user engagement by 47%. Couldn't be more proud of what we achieved together. #ProductManagement #TeamWork",
      image:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800",
      type: "achievement",
    },
    {
      id: 2,
      name: "Rahul Deshmukh",
      role: "Full Stack Developer @ StartupHub",
      text: "Excited to share that our team won the hackathon! 🏆 Built a real-time collaboration tool using React, Node.js, and WebSockets in just 48 hours. Sleep-deprived but incredibly motivated. Thanks to my amazing teammates for making this possible!",
      image:
        "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800",
      type: "achievement",
    },
    {
      id: 3,
      name: "Anjali Mehta",
      role: "UX Designer @ Creative Solutions",
      text: "Thrilled to announce that our app redesign won the UX Excellence Award! 🎨 This project taught me the importance of user empathy and iterative design. Special thanks to the research team for their invaluable insights. #UXDesign #UserExperience",
      image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800",
      type: "award",
    },
    {
      id: 4,
      name: "Vikram Singh",
      role: "Data Scientist @ Analytics Pro",
      text: "Just completed my Machine Learning certification from Stanford! 📊 6 months of dedication and countless hours of practice. Ready to apply these advanced techniques to solve real-world problems. The learning journey never stops! #MachineLearning #DataScience",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800",
      type: "certification",
    },
    {
      id: 5,
      name: "Sneha Patel",
      role: "Marketing Manager @ Growth Labs",
      text: "Our latest campaign achieved 200% ROI! 🎯 Combining data-driven insights with creative storytelling made all the difference. Grateful to work with such a talented team. Here's to setting new benchmarks! #DigitalMarketing #Growth",
      image: "https://images.unsplash.com/photo-1557838923-2985c318be48?w=800",
      type: "campaign",
    },
    {
      id: 6,
      name: "Arjun Rao",
      role: "DevOps Engineer @ Cloud Systems",
      text: "Reduced deployment time from 2 hours to 15 minutes! ⚡ Implemented CI/CD pipelines with Docker and Kubernetes. Automation is truly transformative. Sharing my learnings in a blog post soon. #DevOps #Automation #CloudComputing",
      image:
        "https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=800",
      type: "technical",
    },
    {
      id: 7,
      name: "Kavya Iyer",
      role: "Founder @ EduTech Startup",
      text: "Celebrating a major milestone - 10,000 users! 🎉 What started as a side project is now helping students across India. Thank you to everyone who believed in our vision. This is just the beginning! #Entrepreneurship #EdTech #Startup",
      image:
        "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800",
      type: "milestone",
    },
    {
      id: 8,
      name: "Aditya Kumar",
      role: "iOS Developer @ Mobile First",
      text: "Our app just hit #1 in the App Store! 📱 Months of refinement, user feedback, and late-night debugging sessions paid off. Grateful for the incredible team that made this happen. Onwards and upwards! #iOSDevelopment #AppStore",
      image:
        "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800",
      type: "achievement",
    },
    {
      id: 9,
      name: "Neha Gupta",
      role: "Content Strategist @ Media House",
      text: "Proud to share that our content series reached 1 million views! 🎬 Strategic planning, audience research, and consistent quality made the difference. Thank you to the creative team for bringing these stories to life. #ContentMarketing #DigitalMedia",
      image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800",
      type: "content",
    },
    {
      id: 10,
      name: "Rohan Joshi",
      role: "AI Engineer @ Future Labs",
      text: "Successfully deployed our first AI model to production! 🤖 From research to real-world application - what an incredible journey. Excited to see how this technology will impact our users. The future is here! #ArtificialIntelligence #DeepLearning",
      image:
        "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800",
      type: "technical",
    },
  ];

  const handleCopyText = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === "portfolio") {
      setCopiedPortfolio(true);
      setTimeout(() => setCopiedPortfolio(false), 2000);
    } else {
      setCopiedCertificate(true);
      setTimeout(() => setCopiedCertificate(false), 2000);
    }
  };

  const handleDownloadCertificate = async () => {
    const response = await fetch(certificateDownloadUrl, {
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error("Download failed");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "Frontend_Certificate.pdf";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  };

  const renderCarousel = () => (
    <Swiper
      modules={[Pagination, Autoplay]}
      spaceBetween={20}
      slidesPerView={1}
      pagination={{ clickable: true }}
      autoplay={{ delay: 4000, disableOnInteraction: false }}
      breakpoints={{
        640: { slidesPerView: 2 },
        1024: { slidesPerView: 3 },
      }}
      className="paymentSuccessPage__carousel"
    >
      {mixedCarouselPosts.map((post) => (
        <SwiperSlide key={post.id}>
          <div className="paymentSuccessPage__linkedinCard">
            <div className="paymentSuccessPage__linkedinHeader">
              <div className="paymentSuccessPage__linkedinAvatar">
                {post.name.charAt(0)}
              </div>
              <div className="paymentSuccessPage__linkedinInfo">
                <span className="paymentSuccessPage__linkedinName">
                  {post.name}
                </span>
                <span className="paymentSuccessPage__linkedinRole">
                  {post.role}
                </span>
                <span className="paymentSuccessPage__linkedinTime">2d</span>
              </div>
              <div className="paymentSuccessPage__linkedinLogo">
                <LinkedInIcon />
              </div>
            </div>
            <p className="paymentSuccessPage__linkedinText">{post.text}</p>
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );

  const renderPortfolioSection = () => (
    <div className="paymentSuccessPage__section">
      <div className="paymentSuccessPage__header">
        <CheckCircleIcon />
        <h1 className="paymentSuccessPage__title">
          Your Portfolio is Ready to Share with the World!
        </h1>
        <p className="paymentSuccessPage__subtitle">
          Recruiters love developers who showcase their work professionally.
          They&apos;re constantly on the lookout for talent like you!
        </p>
      </div>

      <div className="paymentSuccessPage__linkSection">
        <h3 className="paymentSuccessPage__sectionTitle">
          🔗 Your Live Portfolio
        </h3>
        <div className="paymentSuccessPage__linkBox">
          <a
            href={portfolioLink}
            target="_blank"
            rel="noopener noreferrer"
            className="paymentSuccessPage__portfolioLink"
          >
            {portfolioLink}
            <ExternalLinkIcon />
          </a>
        </div>
      </div>

      <div className="paymentSuccessPage__shareSection">
        <h3 className="paymentSuccessPage__sectionTitle">
          Share Your Portfolio on LinkedIn
        </h3>
        <p className="paymentSuccessPage__shareSubtitle">
          Copy this ready-to-post content and share your achievement!
        </p>
        <div className="paymentSuccessPage__shareBox">
          <pre className="paymentSuccessPage__shareText">
            {portfolioLinkedInPost}
          </pre>
          <button
            className={`paymentSuccessPage__copyBtn ${
              copiedPortfolio ? "copied" : ""
            }`}
            onClick={() => handleCopyText(portfolioLinkedInPost, "portfolio")}
          >
            <CopyIcon />
            {copiedPortfolio ? "Copied!" : "Copy Text"}
          </button>
        </div>
      </div>

      <div className="paymentSuccessPage__carouselSection">
        <h3 className="paymentSuccessPage__sectionTitle">
          🌟 See What Others Are Sharing
        </h3>
        {renderCarousel()}
      </div>
    </div>
  );

  const renderCertificateSection = () => (
    <div className="paymentSuccessPage__section">
      <div className="paymentSuccessPage__header">
        <CheckCircleIcon />
        <h1 className="paymentSuccessPage__title">
          Your Verified Frontend Certificate is Now Live!
        </h1>
        <p className="paymentSuccessPage__subtitle">
          Stand out from the crowd! Recruiters actively seek certified
          developers to fill their teams.
        </p>
      </div>

      <div className="paymentSuccessPage__downloadSection">
        <h3 className="paymentSuccessPage__sectionTitle">
          📜 Download Your Certificate
        </h3>
        <button
          className="paymentSuccessPage__downloadBtn"
          onClick={handleDownloadCertificate}
        >
          <DownloadIcon />
          Download Frontend Certificate
        </button>
      </div>

      <div className="paymentSuccessPage__shareSection">
        <h3 className="paymentSuccessPage__sectionTitle">
          Share Your Certificate on LinkedIn
        </h3>
        <p className="paymentSuccessPage__shareSubtitle">
          Let your network know about your new certification!
        </p>
        <div className="paymentSuccessPage__shareBox">
          <pre className="paymentSuccessPage__shareText">
            {certificateLinkedInPost}
          </pre>
          <button
            className={`paymentSuccessPage__copyBtn ${
              copiedCertificate ? "copied" : ""
            }`}
            onClick={() =>
              handleCopyText(certificateLinkedInPost, "certificate")
            }
          >
            <CopyIcon />
            {copiedCertificate ? "Copied!" : "Copy Text"}
          </button>
        </div>
      </div>

      <div className="paymentSuccessPage__carouselSection">
        <h3 className="paymentSuccessPage__sectionTitle">
          🌟 See What Others Are Sharing
        </h3>
        {renderCarousel()}
      </div>
    </div>
  );

  const renderBundleSection = () => (
    <>
      <div className="paymentSuccessPage__header">
        <CheckCircleIcon />
        <h1 className="paymentSuccessPage__title">
          Your Portfolio & Certificate Are Ready!
        </h1>
        <p className="paymentSuccessPage__subtitle">
          Everything you need to stand out to recruiters and land your dream
          job.
        </p>
      </div>

      <div className="paymentSuccessPage__bundlePriority">
        <div className="paymentSuccessPage__priorityCard">
          <h3 className="paymentSuccessPage__priorityTitle">
            🔗 Your Live Portfolio
          </h3>
          <div className="paymentSuccessPage__linkBox">
            <a
              href={portfolioLink}
              target="_blank"
              rel="noopener noreferrer"
              className="paymentSuccessPage__portfolioLink"
            >
              {portfolioLink}
              <ExternalLinkIcon />
            </a>
          </div>
        </div>

        <div className="paymentSuccessPage__priorityCard">
          <h3 className="paymentSuccessPage__priorityTitle">
            📜 Your Certificate
          </h3>
          <button
            className="paymentSuccessPage__downloadBtn"
            onClick={handleDownloadCertificate}
          >
            <DownloadIcon />
            Download Certificate
          </button>
        </div>
      </div>

      <div className="paymentSuccessPage__bundleShare">
        <h3 className="paymentSuccessPage__sectionTitle">Share on LinkedIn</h3>
        <p className="paymentSuccessPage__shareSubtitle">
          Amplify your professional presence by sharing both achievements!
        </p>

        <div className="paymentSuccessPage__shareGrid">
          <div className="paymentSuccessPage__shareItem">
            <h4 className="paymentSuccessPage__shareItemTitle">
              Share Your Portfolio on LinkedIn
            </h4>
            <div className="paymentSuccessPage__shareBox">
              <pre className="paymentSuccessPage__shareText">
                {portfolioLinkedInPost}
              </pre>
              <button
                className={`paymentSuccessPage__copyBtn ${
                  copiedPortfolio ? "copied" : ""
                }`}
                onClick={() =>
                  handleCopyText(portfolioLinkedInPost, "portfolio")
                }
              >
                <CopyIcon />
                {copiedPortfolio ? "Copied!" : "Copy Text"}
              </button>
            </div>
          </div>

          <div className="paymentSuccessPage__shareItem">
            <h4 className="paymentSuccessPage__shareItemTitle">
              Share Your Certificate on LinkedIn
            </h4>
            <div className="paymentSuccessPage__shareBox">
              <pre className="paymentSuccessPage__shareText">
                {certificateLinkedInPost}
              </pre>
              <button
                className={`paymentSuccessPage__copyBtn ${
                  copiedCertificate ? "copied" : ""
                }`}
                onClick={() =>
                  handleCopyText(certificateLinkedInPost, "certificate")
                }
              >
                <CopyIcon />
                {copiedCertificate ? "Copied!" : "Copy Text"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="paymentSuccessPage__carouselSection">
        <h3 className="paymentSuccessPage__sectionTitle">
          🌟 See What Others Are Sharing
        </h3>
        {renderCarousel()}
      </div>
    </>
  );

  // Loading / fallback
  if (!paymentData) {
    return (
      <div className="paymentSuccessPage">
        <div className="paymentSuccessPage__container">
          <p>Loading your payment details...</p>
        </div>
      </div>
    );
  }

  // If no known items, show a generic message
  if (!type) {
    return (
      <div className="paymentSuccessPage">
        <div className="paymentSuccessPage__container">
          <p>
            Payment successful, but no portfolio or certificate is linked to
            this transaction. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="paymentSuccessPage">
      <div className="paymentSuccessPage__topbar">
        <div className="paymentSuccessPage__topbarContent">
          <div className="paymentSuccessPage__topbarLogo">
            <img
              src="https://res.cloudinary.com/cqn/image/upload/v1740519381/white_acetechies_logo_vsdhp5.png"
              alt="logo"
              style={{ width: "40px", height: "50px" }}
            />
            <div className="paymentSuccessPage__topbarLogoName">
              <span
                style={{ marginRight: "8px" }}
                className="paymentSuccessPage__topbarBrand"
              >
                AceTechies
              </span>
              <span className="paymentSuccessPage__topbarBrand">Academy</span>
            </div>
          </div>
          <button
            className="paymentSuccessPage__topbarBtn"
            onClick={() => history.push("/dashboard")}
          >
            Go to Dashboard
          </button>
        </div>
      </div>

      <div className="paymentSuccessPage__container">
        {type === "portfolio" && renderPortfolioSection()}
        {type === "certificate" && renderCertificateSection()}
        {type === "bundle" && renderBundleSection()}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
